/**
 * Permission Helper - Provides easy access to user permissions
 *
 * This helper makes it easier to check user permissions without using mapGetters
 */

/**
 * Set user data to localStorage
 * @param {Object} data - The data to set
 * @param {string} data.id - account id
 * @param {string} data.name - account name
 * @param {string} data.email - account email
 * @param {string} data.profile_pic - account profile picture
 */
export const setUser = (data) => {
    const user = {
        id: data.id,
        name: data.name,
        email: data.email,
        profile_pic: data.profile_pic || '/img/default.2c79519a.png',
    }
    localStorage.setItem('user', JSON.stringify(user));
};

/**
 * Get user data from localStorage
 * @returns {Object} The user data
 * @param {string} user.id - user account id
 * @param {string} user.name - user account name
 * @param {string} user.email - user account email
 * @param {string} user.profile_pic - user account profile picture
 */
export const getUser = () => {
    try {
        return JSON.parse(localStorage.getItem('user'));
    } catch (error) {
        console.error('Error getting user data:', error);
        return {};
    }
}

/**
 * Set parent data to localStorage
 * @param {Object} data - The data to set
 * @param {string} data.id - parent account id
 * @param {string} data.name - parent account name
 * @param {string} data.email - parent account email
 * @param {string} data.profile_pic - parent account profile picture
 * @param {string} data.card_verify_flag - parent account card_verify_flag
 * @param {string} data.juristic_verify_flag - parent account juristic_verify_flag
 * @param {string} data.cod_bank_verify_flag - parent account cod_bank_verify_flag
 * @param {string} data.request_support_flag - parent account request_support_flag
 */
export const setParent = (data) => {
    const parent = {
        id: data.id,
        name: data.name,
        email: data.email,
        profile_pic: data.profile_pic || '/img/default.2c79519a.png',
        active_flag: data.active_flag,
        suspended_flag: data.suspended_flag,
        card_verify_flag: data.card_verify_flag,
        juristic_verify_flag: data.juristic_verify_flag,
        cod_bank_verify_flag: data.cod_bank_verify_flag,
        cod_flag: data.cod_flag,
        request_support_flag: data.request_support_flag,
    }
    localStorage.setItem('parent', JSON.stringify(parent));
};

/**
 * Get parent data from localStorage
 * @returns {Object} The parent data
 * @param {string} parent.id - parent account id
 * @param {string} parent.name - parent account name
 * @param {string} parent.email - parent account email
 * @param {string} parent.profile_pic - parent account profile picture
 * @param {string} parent.active_flag - parent account active flag
 * @param {string} parent.suspended_flag - parent account suspended flag
 * @param {string} parent.card_verify_flag - parent account card verify flag
 * @param {string} parent.juristic_verify_flag - parent account juristic verify flag
 * @param {string} parent.cod_bank_verify_flag - parent account cod bank verify flag
 * @param {string} parent.cod_flag - parent account cod flag
 * @param {string} parent.request_support_flag - parent account request support flag
 */
export const getParent = () => {
    try {
        return JSON.parse(localStorage.getItem('parent'));
    } catch (error) {
        console.error('Error getting parent data:', error);
        return {};
    }
}

/**
 * Set wallet data to localStorage
 * @param {Object} data - The data to set
 * @param {Object} data.wallet - The wallet data
 * @param {number} data.wallet.wallet_amount - wallet amount
 * @param {number} data.wallet.credit_usage_amount - credit usage amount
 * @param {number} data.wallet.service_wallet_amount - service wallet amount
 * @param {number} data.wallet.credit_service_usage_amount - credit usage amount
 * @param {number} data.wallet.cod_wallet_amount - cod wallet amount
 * @param {string} data.payment_type - credit type (wallet or credit)
 * @param {Object} data.account_credit - The account credit data
 * @param {number} data.account_credit.credit_limit - credit limit
 */
export const setWallet = (data) => {
     const wallet = {
        wallet_amount: parseFloat(data.wallet?.wallet_amount || 0),
        credit_usage_amount: parseFloat(data.wallet?.credit_usage_amount || 0),
        service_wallet_amount: parseFloat(data.wallet?.service_wallet_amount || 0),
        credit_service_usage_amount: parseFloat(data.wallet?.credit_service_usage_amount || 0),
        cod_wallet_amount: parseFloat(data.wallet?.cod_wallet_amount || 0),
        payment_type: data?.payment_type,
        credit_limit: parseFloat(data?.account_credit?.credit_limit || 0)
    }
    localStorage.setItem('wallet', JSON.stringify(wallet));
};

/**
 * Get wallet data from localStorage
 * @returns {Object} The wallet data
 * @param {number} wallet.delivery - delivery wallet amount
 * @param {number} wallet.service - service wallet amount
 * @param {string} wallet.payment_type - payment type (wallet or credit)
 */
export const getWallet = () => {
    const wallet = {
        delivery: 0,
        service: 0,
        cod: 0,
        payment_type: 'wallet',
        raw_delivery: 0,
        raw_service: 0,
        raw_credit: 0,
    };

    try {
        let rawWallet = JSON.parse(localStorage.getItem('wallet'));

        wallet.payment_type = rawWallet.payment_type;
        wallet.cod = rawWallet.cod_wallet_amount;
        wallet.raw_delivery = rawWallet.wallet_amount;
        wallet.raw_service = rawWallet.service_wallet_amount;
        wallet.raw_credit = rawWallet.credit_limit - rawWallet.credit_usage_amount - rawWallet.credit_service_usage_amount;

        if (rawWallet.payment_type == 'credit') {
            wallet.delivery = rawWallet.credit_limit - rawWallet.credit_usage_amount - rawWallet.credit_service_usage_amount;
            wallet.service = wallet.delivery;
        } else {
            wallet.delivery = rawWallet.wallet_amount;
            wallet.service = rawWallet.service_wallet_amount;
        }
    } catch (error) {
        console.error('Error getting wallet amount:', error);
    }

    return wallet;
}

/**
 * Set permission data to localStorage
 * @param {Object} data - The data to set
 * @param {number} data.id - permission id
 * @param {string} data.access_level_name - permission name
 * @param {string} data.access_level_name_th - permission name th
 * @param {number} data.order_flag - order flag
 * @param {number} data.advanced_order_flag - advanced order flag
 * @param {number} data.report_flag - report flag
 * @param {number} data.advanced_report_flag - advanced report flag
 * @param {number} data.topup_flag - topup flag
 * @param {number} data.advanced_topup_flag - advanced topup flag
 * @param {number} data.finance_flag - finance flag
 * @param {number} data.advanced_finance_flag - advanced finance flag
 * @param {number} data.setting_flag - setting flag
 * @param {number} data.advanced_setting_flag - advanced setting flag
 * @param {number} data.pickup_point_flag - pickup point flag
 * @param {number} data.advanced_pickup_point_flag - advanced pickup point flag
 * @param {number} data.open_api_flag - open api flag
 * @param {number} data.advanced_open_api_flag - advanced open api flag
 * @param {number} data.account_child_flag - account child flag
 * @param {number} data.advanced_account_child_flag - advanced account child flag
 * @param {number} data.cod_flag - cod flag
 * @param {number} data.advanced_cod_flag - advanced cod flag
 * @param {number} data.tax_invoice_flag - tax invoice flag
 * @param {number} data.advanced_tax_invoice_flag - advanced tax invoice flag
 * @param {number} data.credit_flag - credit flag
 * @param {number} data.advanced_credit_flag - advanced credit flag
 */
export const setPermission = (data) => {
    localStorage.setItem('permission', JSON.stringify(data));
};

/**
 * Get permission data from localStorage
 * @returns {Object} The permission data
 */
export const getPermission = () => {
    try {
        return JSON.parse(localStorage.getItem('permission'));
    } catch (error) {
        console.error('Error parsing permission data:', error);
        return {};
    }
};

/**
 * Check if user has access to a specific feature
 * @param {string} feature - The feature to check (e.g., 'order_flag', 'advanced_order_flag', 'shipping_flag', 'finance_flag')
 * @returns {Boolean} true if user has access, false otherwise
 */
export const checkPermission = (feature) => {
    const auth = getPermission();
    if (!auth) return false;

    return Boolean(auth[feature]);
};

export default {
    checkPermission,
    setUser,
    getUser,
    setParent,
    getParent,
    setWallet,
    getWallet,
    setPermission,
    getPermission,
};
